import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
} from '@mui/material';
import RainToday from './RainToday';

interface WeatherResponse {
  name: string;
  weather: {
    description: string;
    icon: string;
  }[];
  main: {
    temp: number;
    feels_like: number;
    humidity: number;
  };
  wind: {
    speed: number;
  };
}

const Weather = () => {
  const [data, setData] = useState<WeatherResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const res = await fetch(
          `https://api.openweathermap.org/data/2.5/weather?lat=52.8408&lon=-6.9261&units=metric&appid=${process.env.REACT_APP_OPENWEATHER_KEY}`
        );

        if (!res.ok) throw new Error('Failed to fetch weather');

        const json = await res.json();
        setData(json);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error || !data) {
    return <Typography color="error">Unable to load weather</Typography>;
  }

  const iconUrl = `https://openweathermap.org/img/wn/${data.weather[0].icon}@2x.png`;

  const stat = (label: string, value: string) => (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: { md: 'space-between' },
          gap: { xs: 0, md: 4 },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            component="img"
            src={iconUrl}
            alt={data.weather[0].description}
            sx={{
              width: { xs: 36, md: 64 },
              height: { xs: 36, md: 64 },
            }}
          />

          <Box>
            <Typography
              variant="h4"
              fontWeight={600}
              sx={{ fontSize: { xs: '1.6rem', md: '2.125rem' } }}
            >
              {Math.round(data.main.temp)}°C
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textTransform: 'capitalize' }}
            >
              {data.weather[0].description}
            </Typography>
          </Box>
        </Stack>

        {/* Stat cells — desktop only */}
        <Stack
          direction="row"
          spacing={4}
          sx={{ display: { xs: 'none', md: 'flex' } }}
        >
          {stat('Feels like', `${Math.round(data.main.feels_like)}°C`)}
          {stat('Humidity', `${data.main.humidity}%`)}
          {stat('Wind', `${Math.round(data.wind.speed)} m/s`)}
          {stat('Location', data.name)}
        </Stack>
      </Box>

      <RainToday />
    </Box>
  );
};

export default Weather;